import React from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { useCart } from "../components/LandingLayout";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const MAX_POLLS = 10;

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clearCart } = useCart();
  const [state, setState] = React.useState("checking"); // checking | paid | pending | error
  const [order, setOrder] = React.useState(null);

  React.useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
        setOrder(data);
        if (data.payment_status === "paid") {
          setState("paid");
          clearCart();
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setState("error");
          return;
        }
        attempts += 1;
        if (attempts >= MAX_POLLS) {
          setState("pending");
          return;
        }
        timer = setTimeout(poll, 2000);
      } catch (e) {
        setState("error");
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="flex min-h-[50vh] items-center justify-center pb-12" data-testid="payment-success-page">
      <div className="max-w-md rounded-3xl border border-neutral-200 bg-white p-8 text-center">
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-neutral-400" />
            <h1 className="mt-4 text-xl font-medium text-neutral-900">
              Confirming your payment…
            </h1>
            <p className="mt-2 text-sm text-neutral-600">
              Please wait a moment, this won&apos;t take long.
            </p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" data-testid="payment-paid-icon" />
            <h1 className="mt-4 text-xl font-medium text-neutral-900">
              Thank you for your order!
            </h1>
            <p className="mt-2 text-sm text-neutral-600">
              Your payment was successful. You&apos;ll receive a confirmation by
              email.
              {order?.shipping_method === "collection"
                ? " We'll be in touch about collection in Tralee."
                : " Your order will be posted to you soon."}
            </p>
            {order?.total != null && (
              <p className="mt-3 text-sm font-medium text-neutral-900">
                Total paid: €{Number(order.total).toFixed(2)}
              </p>
            )}
            <Link
              to="/shop"
              className="mt-6 inline-flex h-10 items-center justify-center rounded-full bg-black px-8 text-[11px] font-medium uppercase tracking-[0.25em] text-white hover:bg-neutral-800"
              data-testid="success-continue-shopping"
            >
              Continue shopping
            </Link>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 className="mx-auto h-10 w-10 text-amber-500" />
            <h1 className="mt-4 text-xl font-medium text-neutral-900">
              Payment is processing
            </h1>
            <p className="mt-2 text-sm text-neutral-600">
              We&apos;re still confirming your payment. You&apos;ll get an email once
              it&apos;s complete.
            </p>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="mx-auto h-12 w-12 text-red-500" />
            <h1 className="mt-4 text-xl font-medium text-neutral-900">
              Something went wrong
            </h1>
            <p className="mt-2 text-sm text-neutral-600">
              We couldn&apos;t confirm this payment. If you were charged, please
              contact us and we&apos;ll sort it out.
            </p>
            <Link
              to="/shop"
              className="mt-6 inline-flex h-10 items-center justify-center rounded-full border border-neutral-900 px-8 text-[11px] font-medium uppercase tracking-[0.25em] text-neutral-900 hover:bg-neutral-900 hover:text-white"
            >
              Back to shop
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
